import React, { useState, useEffect, useMemo } from 'react';
import { X, Download, ExternalLink, FileText, Loader, FileQuestion } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../services/api';
import './DocumentViewerModal.css';

// Extensions browsers can render natively inside an <iframe>.
const IFRAME_EXTS = new Set(['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'txt', 'csv']);
// Spreadsheet extensions we render ourselves as HTML tables via SheetJS.
const SHEET_EXTS = new Set(['xlsx', 'xls', 'csv']);

function getExt(filename = '') {
  const parts = filename.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

export default function DocumentViewerModal({ doc, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [error, setError] = useState(false);
  // Spreadsheet state: parsed workbook → sheet name → 2D array of rows.
  const [workbook, setWorkbook] = useState(null);
  const [activeSheet, setActiveSheet] = useState(null);

  const ext = getExt(doc?.filename);
  const kind = SHEET_EXTS.has(ext) ? 'sheet' : IFRAME_EXTS.has(ext) ? 'iframe' : 'unsupported';

  useEffect(() => {
    if (!doc?.jobId) return;
    let objectUrl;
    let cancelled = false;

    // Reset state when switching documents.
    setBlobUrl(null);
    setWorkbook(null);
    setActiveSheet(null);
    setIframeLoaded(false);
    setError(false);

    const responseType = kind === 'sheet' ? 'arraybuffer' : 'blob';

    api.get(`/api/documents/${doc.jobId}/preview`, { responseType })
      .then(res => {
        if (cancelled) return;
        if (kind === 'sheet') {
          const wb = XLSX.read(res.data, { type: 'array' });
          setWorkbook(wb);
          setActiveSheet(wb.SheetNames[0] || null);
        } else {
          objectUrl = URL.createObjectURL(res.data);
          setBlobUrl(objectUrl);
        }
      })
      .catch(() => { if (!cancelled) setError(true); });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc?.jobId, kind]);

  // Convert the active sheet to a 2D array of rows for rendering.
  const sheetRows = useMemo(() => {
    if (!workbook || !activeSheet) return null;
    const ws = workbook.Sheets[activeSheet];
    if (!ws) return null;
    return XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: false });
  }, [workbook, activeSheet]);

  if (!doc) return null;

  // For spreadsheet downloads we need a real blob; fetch lazily on demand.
  const handleDownload = async () => {
    try {
      const res = await api.get(`/api/documents/${doc.jobId}/preview`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError(true);
    }
  };

  const handleOpenExternal = async () => {
    if (blobUrl) { window.open(blobUrl, '_blank'); return; }
    try {
      const res = await api.get(`/api/documents/${doc.jobId}/preview`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setError(true);
    }
  };

  const loading = !error && (kind === 'sheet' ? !workbook : kind === 'iframe' ? !blobUrl : false);

  return (
    <div className="dvm-overlay" onClick={onClose}>
      <div className="dvm-modal" onClick={e => e.stopPropagation()}>
        <div className="dvm-header">
          <div className="dvm-title">
            <FileText size={16} />
            <span>{doc.filename}</span>
          </div>
          <div className="dvm-actions">
            <button className="dvm-btn" onClick={handleDownload} title="Download">
              <Download size={15} />
              Download
            </button>
            <button className="dvm-btn" onClick={handleOpenExternal} title="Open in new tab">
              <ExternalLink size={15} />
              Open
            </button>
            <button className="dvm-close" onClick={onClose} title="Close">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Sheet tabs — only when a workbook has more than one sheet */}
        {kind === 'sheet' && workbook && workbook.SheetNames.length > 1 && (
          <div className="dvm-tabs">
            {workbook.SheetNames.map(name => (
              <button
                key={name}
                className={`dvm-tab ${name === activeSheet ? 'active' : ''}`}
                onClick={() => setActiveSheet(name)}
                title={name}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        <div className="dvm-body">
          {loading && (
            <div className="dvm-loading">
              <Loader size={24} className="dvm-spinner" />
              <span>Loading document…</span>
            </div>
          )}

          {error && (
            <div className="dvm-loading">
              <span>Failed to load document.</span>
            </div>
          )}

          {!error && kind === 'iframe' && blobUrl && (
            <iframe
              src={blobUrl}
              title={doc.filename}
              className="dvm-iframe"
              style={{ opacity: iframeLoaded ? 1 : 0 }}
              onLoad={() => setIframeLoaded(true)}
            />
          )}

          {!error && kind === 'sheet' && sheetRows && (
            <div className="dvm-sheet-scroll">
              {sheetRows.length === 0 ? (
                <div className="dvm-loading"><span>This sheet is empty.</span></div>
              ) : (
                <table className="dvm-sheet-table">
                  <tbody>
                    {sheetRows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          ri === 0
                            ? <th key={ci}>{String(cell)}</th>
                            : <td key={ci}>{String(cell)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {!error && kind === 'unsupported' && (
            <div className="dvm-loading dvm-unsupported">
              <FileQuestion size={40} />
              <span>Preview isn’t available for <strong>.{ext || 'this'}</strong> files.</span>
              <span className="dvm-unsupported-hint">Use Download or Open to view it in another app.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
